import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiShield, FiGlobe, FiDatabase, FiCpu, FiCheckCircle, FiAlertTriangle, 
  FiSearch, FiFileText, FiActivity, FiTarget, FiChevronDown,
} = FiIcons;

const phases = [
  {
    id: '01',
    icon: FiSearch,
    title: 'Entity Intelligence & Target Resolution',
    badge: 'Phase One',
    color: 'text-sonar-green',
    borderColor: 'border-sonar-green',
    bgColor: 'bg-sonar-green/10',
    summary: 'Meticulous verification of legal entity identity before intelligence gathering commences.',
    detail: `The foundation of any credit assessment is the unambiguous identification of the subject entity. OMEN v4 begins by resolving the full legal registered name against global corporate registries. This phase eliminates brand-entity confusion and ensures that all subsequent data retrieval is routed to the correct regulatory jurisdiction and functional reporting currency. If resolution confidence falls below institutional thresholds, the process pauses for manual disambiguation.`,
    bullets: [
      'Multi-registry cross-referencing across 200+ jurisdictions',
      'Legal name resolution and corporate structure mapping',
      'Functional reporting currency confirmation at the registration source',
      'Target confidence scoring with automatic safety halt',
      'Jurisdiction-specific regulatory framework identification'
    ],
  },
  {
    id: '02',
    icon: FiDatabase,
    title: 'RAG-First Data Retrieval Protocol',
    badge: 'Phase Two',
    color: 'text-sonar-green',
    borderColor: 'border-sonar-green',
    bgColor: 'bg-sonar-green/10',
    summary: 'A strict retrieval-augmented generation mandate ensuring zero reliance on static memory.',
    detail: `The system operates on an absolute RAG-first principle. It is forbidden from using pre-trained model memory for factual data. Instead, it executes time-bound queries across a tiered hierarchy of trusted sources. Tier 1 sources (official filings and registry submissions) are prioritized. For every data point, the agent follows a 3-attempt retry logic with automatic query reformulation before flagging a data gap.`,
    bullets: [
      'Mandatory live retrieval for all financial and regulatory data',
      'Strict Tier-1 sourcing (SEC EDGAR, IR pages, Official Registries)',
      '3-attempt iterative search protocol to maximize data acquisition',
      'Dynamic time-bound queries ensuring data freshness',
      'Zero-memory policy for factual reporting'
    ],
  },
  {
    id: '03',
    icon: FiActivity,
    title: 'Multi-Vector Cross-Validation',
    badge: 'Phase Three',
    color: 'text-sonar-green',
    borderColor: 'border-sonar-green',
    bgColor: 'bg-sonar-green/10',
    summary: 'The Triangle Rule: material claims require independent multi-source corroboration.',
    detail: `Retrieved data is scrutinized through a structured validation loop. The Triangle Rule mandates that material metrics require agreement from multiple independent sources to achieve HIGH confidence. Discrepancies are handled through a rule-based hierarchy where primary source filings override aggregators. Every resolved conflict is logged for complete transparency.`,
    bullets: [
      'Multi-source triangulation for all material metrics',
      'Rule-based conflict resolution using source tier hierarchy',
      'Confidence scoring (HIGH / MEDIUM / LOW) determined by consensus',
      'Stale data flagging for documents exceeding 18 months',
      'Systematic identification and logging of data gaps'
    ],
  },
  {
    id: '04',
    icon: FiShield,
    title: 'Intelligence Synthesis & Guardrails',
    badge: 'Phase Four',
    color: 'text-sonar-green',
    borderColor: 'border-sonar-green',
    bgColor: 'bg-sonar-green/10',
    summary: 'Final memo construction using only verified data through a sterile corridor.',
    detail: `The final phase translates validated data into a comprehensive credit memo. This occurs within a "sterile corridor" where the system is prohibited from introducing any new information not present in the validation ledger. All factual claims are accompanied by granular inline citations, and analytical inferences are explicitly flagged. The result is a transparent, verifiable, and institutional-grade intelligence package.`,
    bullets: [
      'Narrative synthesis restricted to validated data ledger',
      'Mandatory inline citations (Tier, URL, Date) for all claims',
      'Explicit separation of verified fact from [INFERENCE]',
      'Strict adherence to functional reporting currency',
      'Comprehensive audit trail from retrieval to final report'
    ],
  }
];

const PhaseCard = ({ phase, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      whileInView={{ opacity: 1, y: 0 }} 
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className={`bg-sonar-card border rounded-2xl overflow-hidden transition-all duration-300 ${open ? phase.borderColor : 'border-sonar-border hover:border-sonar-green/40'}`}
    >
      <button onClick={() => setOpen(o => !o)} className="w-full text-left p-6 flex items-start gap-5 group">
        <div className={`w-12 h-12 rounded-xl ${phase.bgColor} flex items-center justify-center shrink-0 border ${phase.borderColor} mt-0.5`}>
          <SafeIcon icon={phase.icon} className={`text-xl ${phase.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <span className={`text-[9px] font-black uppercase tracking-[0.25em] font-mono ${phase.color}`}>{phase.badge}</span>
          </div>
          <h3 className="text-base font-black text-white uppercase tracking-wide leading-tight mb-2">{phase.title}</h3>
          <p className="text-sm text-slate-400 font-mono leading-relaxed pr-8">{phase.summary}</p>
        </div>
        <SafeIcon icon={FiChevronDown} className={`text-slate-500 shrink-0 mt-1 transition-transform duration-300 ${open ? 'rotate-180 ' + phase.color : 'group-hover:text-sonar-green'}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-6 pb-6 border-t border-sonar-border pt-5 grid md:grid-cols-2 gap-6">
              <div>
                <p className={`text-[9px] font-black uppercase tracking-[0.2em] font-mono mb-3 ${phase.color}`}>System Protocol</p>
                <div className="text-sm text-slate-400 font-mono leading-relaxed">{phase.detail}</div>
              </div>
              <div>
                <p className={`text-[9px] font-black uppercase tracking-[0.2em] font-mono mb-3 ${phase.color}`}>Key Guardrails</p>
                <ul className="space-y-2">
                  {phase.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-300 font-mono">
                      <SafeIcon icon={FiCheckCircle} className={`shrink-0 mt-0.5 ${phase.color}`} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const MethodologyPage = () => {
  return (
    <div className="max-w-5xl mx-auto px-4 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-20">
        <div className="inline-flex items-center gap-2 bg-sonar-green/10 text-sonar-green px-4 py-1.5 rounded-full text-[10px] font-black border border-sonar-green/30 tracking-[0.25em] uppercase mb-6">
          <SafeIcon icon={FiCpu} className="animate-pulse" /> OMEN Intelligence Stack
        </div>
        <h1 className="text-5xl md:text-6xl font-black text-white leading-none mb-6 tracking-tight uppercase">
          System <span className="text-sonar-green">Protocol.</span>
        </h1>
        <p className="text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed font-mono">
          The OMEN v4 engine is built on a four-phase intelligence verification protocol that prioritizes verifiable data over probabilistic synthesis.
        </p>
      </motion.div>

      <div className="space-y-4 mb-20">
        {phases.map((phase, i) => (
          <PhaseCard key={phase.id} phase={phase} index={i} />
        ))}
      </div>

      <div className="text-center p-10 bg-sonar-card border border-sonar-border rounded-3xl">
        <div className="w-14 h-14 rounded-full bg-sonar-green/10 border border-sonar-green/30 flex items-center justify-center mx-auto mb-5">
          <SafeIcon icon={FiTarget} className="text-sonar-green text-2xl" />
        </div>
        <h3 className="text-2xl font-black text-white uppercase tracking-tight mb-3">Institutional Data Transparency</h3>
        <p className="text-sm text-slate-400 font-mono max-w-md mx-auto mb-6 leading-relaxed">
          Every report generated by OMEN is a transparent record of verified facts. We show you exactly what we found, where we found it, and how we validated it.
        </p>
        <div className="flex items-center justify-center gap-6">
          <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500 uppercase tracking-widest">
            <div className="w-1.5 h-1.5 rounded-full bg-sonar-green" /> RAG-First
          </div>
          <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500 uppercase tracking-widest">
            <div className="w-1.5 h-1.5 rounded-full bg-sonar-green" /> Source-Verified
          </div>
          <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500 uppercase tracking-widest">
            <div className="w-1.5 h-1.5 rounded-full bg-sonar-green" /> Zero-Memory
          </div>
        </div>
      </div>
    </div>
  );
};

export default MethodologyPage;